<script setup lang="ts">
/**
 * Abstrahierte Bot-Nachricht IM Ticket — bewusst kein Discord-Replica,
 * sondern eine Skizze im Vokabular der Seite:
 *
 *   ┌ Thread-Kopf: der Ticket-Name (→ „das passiert im Ticket")
 *   ├ angedeutete Member-Nachricht: Avatar + zwei stumme Textzeilen
 *   └ die Nachricht des Bots: Avatar · Name · APP · Zeit, darunter die
 *     Components-v2-Karte (Akzentbalken, Titel, Name→Wert, Buttons)
 *
 * Damit ist ohne Erklärtext lesbar: EIN Bot hat das als Karte in die
 * Konversation gestellt. Farben/Typo kommen aus den Seiten-Tokens.
 */
interface PeekRow {
  key: string
  value: string
  mono?: boolean
  badge?: boolean
}

withDefaults(defineProps<{
  /** Ticket-/Thread-Name im Kopf */
  thread?: string
  author?: string
  time?: string
  /** angedeutete Member-Nachricht vor der Bot-Nachricht */
  ghost?: boolean
  title: string
  accent?: string
  rows?: PeekRow[]
  /** Button-Labels unter der Karte (Link-Buttons der echten Karte) */
  actions?: string[]
}>(), {
  thread: undefined,
  author: 'Clarify',
  time: '2:31 PM',
  ghost: true,
  accent: 'var(--nb-accent-soft)',
  rows: () => [],
  actions: () => []
})
</script>

<template>
  <div class="peek" :style="{ '--peek-accent': accent }">
    <div v-if="thread" class="peek-head">
      <UIcon name="i-lucide-message-square-text" class="size-[13px]" aria-hidden="true" />
      <span class="peek-head-name">{{ thread }}</span>
      <span class="peek-head-meta">ticket</span>
    </div>

    <div v-if="ghost" class="peek-ghost" aria-hidden="true">
      <span class="peek-avatar peek-avatar-ghost" />
      <span class="peek-ghost-lines">
        <span class="peek-ghost-line is-short" />
        <span class="peek-ghost-line is-long" />
      </span>
    </div>

    <div class="peek-msg">
      <span class="peek-avatar peek-avatar-bot" aria-hidden="true">
        <UIcon name="i-lucide-bot" class="size-[14px]" />
      </span>
      <div class="peek-body">
        <div class="peek-meta">
          <span class="peek-author">{{ author }}</span>
          <span class="peek-app">APP</span>
          <span class="peek-time">{{ time }}</span>
        </div>
        <div class="peek-card">
          <div class="peek-card-title">{{ title }}</div>
          <slot />
          <div v-if="rows.length" class="peek-rows">
            <div v-for="r in rows" :key="r.key" class="peek-row">
              <span class="peek-key">{{ r.key }}</span>
              <span class="peek-value" :class="{ 'is-mono': r.mono, 'is-badge': r.badge }">{{ r.value }}</span>
            </div>
          </div>
          <div v-if="actions.length" class="peek-actions">
            <span v-for="a in actions" :key="a" class="peek-action">
              {{ a }}
              <UIcon name="i-lucide-arrow-up-right" class="size-[11px]" aria-hidden="true" />
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.peek {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 14px 14px;
  background: var(--nb-bg);
  border: 1px solid var(--nb-hairline);
  border-radius: var(--radius-md);
  font-size: 0.8rem;
  line-height: 1.4;
}

/* Thread-Kopf: der Ticket-Name */
.peek-head {
  display: flex;
  align-items: center;
  gap: 7px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--nb-hairline);
  color: var(--nb-ink-muted);
  font-size: 0.78rem;
  font-weight: 600;
}

.peek-head-name {
  color: var(--nb-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.peek-head-meta {
  margin-left: auto;
  font-family: var(--font-mono);
  font-size: 0.6rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nb-ink-faint);
}

/* Angedeutete Member-Nachricht */
.peek-ghost {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  opacity: 0.75;
}

.peek-avatar {
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
}

.peek-avatar-ghost {
  background: var(--nb-elevated);
  border: 1px solid var(--nb-hairline);
}

.peek-avatar-bot {
  background: var(--nb-accent-tint);
  border: 1px solid var(--nb-accent-border);
  color: var(--nb-accent-soft);
}

.peek-ghost-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 5px;
}

.peek-ghost-line {
  height: 7px;
  border-radius: var(--radius-pill);
  background: var(--nb-hairline-strong);
}

.peek-ghost-line.is-short {
  width: 34%;
}

.peek-ghost-line.is-long {
  width: 70%;
}

/* Die Bot-Nachricht */
.peek-msg {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.peek-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.peek-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
  padding-top: 5px;
}

.peek-author {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--nb-ink);
}

.peek-app {
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 2px 4px;
  border-radius: 3px;
  background: var(--nb-accent-tint-strong);
  border: 1px solid var(--nb-accent-border);
  color: var(--nb-accent-soft);
}

.peek-time {
  font-size: 0.68rem;
  color: var(--nb-ink-faint);
}

/* Die Components-v2-Karte */
.peek-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px 10px 14px;
  background: var(--nb-surface);
  border: 1px solid var(--nb-hairline);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.peek-card::before {
  content: '';
  position: absolute;
  inset-block: 0;
  left: 0;
  width: 3px;
  background: var(--peek-accent);
}

.peek-card-title {
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: var(--nb-ink);
}

.peek-rows {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.peek-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  font-size: 0.78rem;
}

.peek-key {
  color: var(--nb-ink-faint);
}

.peek-value {
  color: var(--nb-ink);
  font-weight: 500;
  text-align: right;
}

.peek-value.is-mono {
  font-family: var(--font-mono);
  font-size: 0.72rem;
}

.peek-value.is-badge {
  padding: 1px 8px;
  border-radius: var(--radius-pill);
  background: var(--nb-accent-tint-strong);
  border: 1px solid var(--nb-accent-border);
  color: var(--nb-accent-soft);
  font-size: 0.7rem;
  font-weight: 600;
}

.peek-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.peek-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px;
  border-radius: 6px;
  background: var(--nb-elevated);
  border: 1px solid var(--nb-hairline-strong);
  color: var(--nb-ink-muted);
  font-size: 0.7rem;
  font-weight: 500;
}
</style>
